<script setup lang="ts">
import type {
	CarouselEmits,
	CarouselProps,
	WithClassAsProps
} from './interface';

import { cn } from '@/utils';

import { useProvideCarousel } from './useCarousel';

const props = withDefaults(defineProps<CarouselProps & WithClassAsProps>(), {
	orientation: 'horizontal',
	disableKeyboard: false
});

const emits = defineEmits<CarouselEmits>();

const {
	canScrollNext,
	canScrollPrev,
	carouselApi,
	carouselRef,
	orientation,
	scrollNext,
	scrollPrev
} = useProvideCarousel(props, emits);

defineExpose({
	canScrollNext,
	canScrollPrev,
	carouselApi,
	carouselRef,
	orientation,
	scrollNext,
	scrollPrev
});

function onKeyDown(event: KeyboardEvent) {
	if (props.disableKeyboard) return;

	const prevKey = props.orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft';
	const nextKey = props.orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight';

	if (event.key === prevKey) {
		event.preventDefault();
		event.stopPropagation();
		scrollPrev();

		return;
	}

	if (event.key === nextKey) {
		event.preventDefault();
		event.stopPropagation();
		scrollNext();
	}
}
</script>

<template>
	<div
		:class="cn('relative', props.class)"
		role="region"
		aria-roledescription="carousel"
		:tabindex="props.disableKeyboard ? -1 : 0"
		@keydown="onKeyDown"
	>
		<slot
			:can-scroll-next
			:can-scroll-prev
			:carousel-api
			:carousel-ref
			:orientation
			:scroll-next
			:scroll-prev
		></slot>
	</div>
</template>
